// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import UploadRequestsBadge from './UploadRequestsBadge';

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  dashboard: getIcon('ic_dashboard'),
  user: getIcon('ic_user'),
  banking: getIcon('ic_banking'),
  invoice: getIcon('ic_invoice'),
  calendar: getIcon('ic_calendar'),
  kanban: getIcon('ic_kanban'),
  cart: getIcon('ic_cart'),
  booking: getIcon('ic_booking'),
  analytics: getIcon('ic_analytics'),
  mail: getIcon('ic_mail'),
  chat: getIcon('ic_chat'),
  ecommerce: getIcon('ic_ecommerce'),
};

const navConfig = [
  // ----------------------------------------------------------------------
  // DASHBOARD
  // ----------------------------------------------------------------------
  {
    subheader: 'CORE ADMIN',
    items: [
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.overview,
        icon: ICONS.dashboard,
        children: [
          { title: 'Overview', path: PATH_DASHBOARD.overview },
          { title: 'Clients Summary', path: `${PATH_DASHBOARD.overview}#clients-summary` },
          { title: 'Subscriptions Summary', path: `${PATH_DASHBOARD.overview}#subscriptions-summary` },
          { title: 'Revenue Summary', path: `${PATH_DASHBOARD.overview}#revenue-summary` },
          { title: 'Recent Activity', path: `${PATH_DASHBOARD.overview}#recent-activity` },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------------
  // CLIENTS
  // ----------------------------------------------------------------------
  {
    subheader: 'CLIENT MANAGEMENT',
    items: [
      {
        title: 'All Clients',
        path: PATH_DASHBOARD.clients.all,
        icon: ICONS.user,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // AGENTS
  // ----------------------------------------------------------------------
  {
    subheader: 'AGENT MANAGEMENT',
    items: [
      {
        title: 'Field Agents',
        path: PATH_DASHBOARD.agents.root,
        icon: ICONS.booking,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // APP USERS: upload requests and updates (announcements)
  // ----------------------------------------------------------------------
  {
    subheader: 'APP USERS',
    items: [
      {
        title: 'Upload Requests',
        path: PATH_DASHBOARD.uploadRequests.root,
        icon: ICONS.invoice,
        info: <UploadRequestsBadge />,
      },
      {
        title: 'Upload PDF',
        path: PATH_DASHBOARD.rollLibrary.root,
        icon: ICONS.invoice,
      },
      {
        title: 'Updates',
        path: PATH_DASHBOARD.announcements.root,
        icon: ICONS.chat,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // WEBSITE LEADS
  // ----------------------------------------------------------------------
  {
    subheader: 'WEBSITE LEADS',
    items: [
      {
        title: 'Demo / Quote Queries',
        path: PATH_DASHBOARD.websiteLeads.queries,
        icon: ICONS.mail,
      },
      {
        title: 'Partner Requests',
        path: PATH_DASHBOARD.websiteLeads.partnerRequests,
        icon: ICONS.chat,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // VOTER SIGNS
  // ----------------------------------------------------------------------
  {
    subheader: 'ELECTION ASSETS',
    items: [
      {
        title: 'Voter Signs',
        path: PATH_DASHBOARD.voterSigns.root,
        icon: ICONS.ecommerce,
      },
      {
        title: 'Polling Schedules',
        path: PATH_DASHBOARD.pollingSchedules.root,
        icon: ICONS.calendar,
      },
      {
        title: 'Locations',
        path: PATH_DASHBOARD.locations.root,
        icon: ICONS.kanban,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PLANS & SUBSCRIPTIONS
  // ----------------------------------------------------------------------
  {
    subheader: 'PLANS & SUBSCRIPTIONS',
    items: [
      {
        title: 'Plans',
        path: PATH_DASHBOARD.plansAndSubscriptions.plans,
        icon: ICONS.kanban,
      },
      {
        title: 'Subscriptions',
        path: PATH_DASHBOARD.plansAndSubscriptions.subscriptions,
        icon: ICONS.booking,
      },
      {
        title: 'Free Trial Limits',
        path: PATH_DASHBOARD.plansAndSubscriptions.trialSettings,
        icon: ICONS.analytics,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PROMO CODES
  // ----------------------------------------------------------------------
  {
    subheader: 'PROMO CODES',
    items: [
      {
        title: 'Promo Codes',
        path: PATH_DASHBOARD.promoCodes.root,
        icon: ICONS.cart,
      },
      {
        title: 'Partner Code Percent',
        path: PATH_DASHBOARD.promoCodes.partnerCodeSettings,
        icon: ICONS.cart,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PAYMENTS & INVOICES
  // ----------------------------------------------------------------------
  {
    subheader: 'PAYMENTS & BILLING',
    items: [
      {
        title: 'Payments',
        path: PATH_DASHBOARD.paymentsAndInvoices.root,
        icon: ICONS.banking,
      },
      {
        title: 'Invoices',
        path: PATH_DASHBOARD.paymentsAndInvoices.invoices,
        icon: ICONS.invoice,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PARTNERS
  // ----------------------------------------------------------------------
  {
    subheader: 'PARTNER PROGRAM',
    items: [
      {
        title: 'Partners',
        path: PATH_DASHBOARD.partners.all,
        icon: ICONS.user,
      },
      {
        title: 'Partner Orders',
        path: PATH_DASHBOARD.partners.orders,
        icon: ICONS.cart,
      },
      {
        title: 'Location Requests',
        path: PATH_DASHBOARD.partners.locationRequests,
        icon: ICONS.booking,
      },
      {
        title: 'Commissions',
        path: PATH_DASHBOARD.commissions.rules,
        icon: ICONS.banking,
        children: [
          { title: 'Rules', path: PATH_DASHBOARD.commissions.rules },
          { title: 'Ledger', path: PATH_DASHBOARD.commissions.ledger },
          { title: 'Payouts', path: PATH_DASHBOARD.commissions.payouts },
          { title: 'Reports & Exports', path: PATH_DASHBOARD.commissions.reports },
          { title: 'Settings', path: PATH_DASHBOARD.commissions.settings },
        ],
      },
      {
        title: 'Partner KYC',
        path: PATH_DASHBOARD.kyc.queue,
        icon: ICONS.invoice,
        children: [
          { title: 'Review Queue', path: PATH_DASHBOARD.kyc.queue },
          { title: 'Settings', path: PATH_DASHBOARD.kyc.settings },
        ],
      },
      {
        title: 'Material Wallets',
        path: PATH_DASHBOARD.wallets.overview,
        icon: ICONS.banking,
        children: [
          { title: 'Overview & Summary', path: PATH_DASHBOARD.wallets.overview },
          { title: 'All Wallets', path: PATH_DASHBOARD.wallets.all },
          { title: 'Recharges', path: PATH_DASHBOARD.wallets.recharges },
          { title: 'Reconciliation', path: PATH_DASHBOARD.wallets.reconcile },
          { title: 'Wallet Settings', path: PATH_DASHBOARD.wallets.settings },
        ],
      },
      {
        title: 'Material Orders',
        path: PATH_DASHBOARD.material.orders,
        icon: ICONS.cart,
        children: [
          { title: 'Orders List', path: PATH_DASHBOARD.material.orders },
          { title: 'Reports & Exports', path: PATH_DASHBOARD.material.reports },
          { title: 'Stuck Jobs Queue', path: PATH_DASHBOARD.material.stuck },
          { title: 'Postcards Monitor', path: PATH_DASHBOARD.material.postcards },
          { title: 'Voter Data', path: PATH_DASHBOARD.material.slipData },
          { title: 'Products & Rates', path: PATH_DASHBOARD.material.products },
          { title: 'Material Settings', path: PATH_DASHBOARD.material.settings },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------------
  // DELETED ACCOUNTS (log of every partner / client / agent delete)
  // ----------------------------------------------------------------------
  {
    subheader: 'ACCOUNTS',
    items: [
      {
        title: 'Deleted Accounts',
        path: PATH_DASHBOARD.deletedAccounts.root,
        icon: ICONS.user,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // STAFF & SECURITY (migration 0037: staff logins, activity log, login history)
  // ----------------------------------------------------------------------
  {
    subheader: 'STAFF & SECURITY',
    items: [
      {
        title: 'Staff Users',
        path: PATH_DASHBOARD.staff.root,
        icon: ICONS.user,
      },
      {
        title: 'Activity Log',
        path: PATH_DASHBOARD.audit.activity,
        icon: ICONS.analytics,
      },
      {
        title: 'Login History',
        path: PATH_DASHBOARD.audit.logins,
        icon: ICONS.booking,
      },
      {
        title: 'Two-Factor Login',
        path: `${PATH_DASHBOARD.settings.root}?tab=security`,
        icon: ICONS.kanban,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // SETTINGS
  // ----------------------------------------------------------------------
  {
    subheader: 'SYSTEM SETTINGS',
    items: [
      {
        title: 'Settings',
        path: PATH_DASHBOARD.settings.root,
        icon: ICONS.calendar,
        children: [
          { title: 'General', path: `${PATH_DASHBOARD.settings.root}?tab=general` },
          { title: 'Business Information', path: `${PATH_DASHBOARD.settings.root}?tab=business` },
          { title: 'Payment Settings', path: `${PATH_DASHBOARD.settings.root}?tab=payment` },
          { title: 'Notification Settings', path: `${PATH_DASHBOARD.settings.root}?tab=notification` },
          { title: 'Security Settings', path: `${PATH_DASHBOARD.settings.root}?tab=security` },
          { title: 'Social Platforms', path: PATH_DASHBOARD.settings.socialPlatforms },
        ],
      },
    ],
  },
];

export default navConfig;
